import { useEffect, useState } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { Plus, PencilSquare, QrCodeScan, CarFront } from "react-bootstrap-icons";
import { QRCodeCanvas } from "qrcode.react";
import Swal from 'sweetalert2';
import api from "../api/axios";

export default function PersonPage() {
  const [persons, setPersons] = useState([]);
  const [filteredPersons, setFilteredPersons] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [totpData, setTotpData] = useState(null);

  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showTotpModal, setShowTotpModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showVehiclesModal, setShowVehiclesModal] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);


  const [loadingTable, setLoadingTable] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingTotp, setLoadingTotp] = useState(null);

  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    id: null,
    tenant_id: "",
    name: "",
    document_type: "",
    document_num: "",
    phone: "",
    email: "",
    license_num: "",
    address: "",
    city: "",
    mobile_number: "",
    eps: "",
    arl: "",
    afp: "",
    contract_type: "",
    is_driver: false,
    is_admin: false,
    is_owner: false,
    is_active: true,
  });

  // ==============================
  // Fetch inicial
  // ==============================
  useEffect(() => {
    fetchPersons();
    fetchTenants();
  }, []);

  const normalizeText = (text) => {
    return text
      ?.toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  };

  const fetchPersons = async () => {
    try {
      setLoadingTable(true);
      const res = await api.get("/persons/list");
      setPersons(res.data.data);
      setFilteredPersons(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingTable(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const res = await api.get("/tenants/accessible");
      setTenants(res.data.data);
    } catch (err) {
      console.error(err);
    }
  };

  // ==============================
  // Filtrado
  // ==============================
  useEffect(() => {
    const term = normalizeText(search).trim();
    const result = persons.filter((p) => {
      // ======================
      // BÚSQUEDA POR TEXTO
      // ======================
      const matchesText =
        normalizeText(p.name)?.includes(term) ||
        normalizeText(p.document_num)?.includes(term) ||
        normalizeText(p.tenant?.name)?.includes(term) ||
        normalizeText(p.mobile_number)?.includes(term) ||
        normalizeText(p.document_type)?.includes(term);

      // ======================
      // BÚSQUEDA POR ESTADO
      // ======================
      const matchesStatus =
        ("activo".startsWith(term) && p.is_active === true) ||
        ("inactivo".startsWith(term) && p.is_active === false);


      // 👇 si el input está vacío, muestra todo
      if (!term) return true;

      // 👇 prioriza estado si coincide exactamente
      if ("activo".startsWith(term) || "inactivo".startsWith(term)) {
        return matchesStatus;
      }

      return matchesText;
    });

    setFilteredPersons(result);
  }, [search, persons]);

  // ==============================
  // Modal (Nuevo)
  // ==============================
  const handleShowNew = () => {
    setIsEditing(false);
    setErrors({});

    setFormData({
      id: null,
      tenant_id: "",
      name: "",
      document_type: "",
      document_num: "",
      phone: "",
      email: "",
      license_num: "",
      address: "",
      city: "",
      mobile_number: "",
      eps: "",
      arl: "",
      afp: "",
      contract_type: "",
      is_driver: false,
      is_admin: false,
      is_owner: false,
      is_active: true,
    });

    setShowModal(true);
  };

  // ==============================
  // Modal (Editar)
  // ==============================
  const handleShowEdit = (p) => {
    setIsEditing(true);
    setErrors({});

    setFormData({
      id: p.id,
      tenant_id: p.tenant_id,
      name: p.name,
      document_type: p.document_type,
      document_num: p.document_num,
      phone: p.phone,
      email: p.email,
      license_num: p.license_num,
      address: p.address,
      city: p.city,
      mobile_number: p.mobile_number,
      eps: p.eps,
      arl: p.arl,
      afp: p.afp,
      contract_type: p.contract_type,
      is_driver: p.is_driver,
      is_admin: p.is_admin,
      is_owner: p.is_owner,
      is_active: p.is_active,
    });

    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setIsEditing(false);
    // fetchPersons();
  };

  // ==============================
  // Guardar (Crear / Editar)
  // ==============================
  const handleSave = async () => {
    setLoadingSave(true);
    setErrors({}); // siempre limpiar errores antes de enviar

    const payload = {
      ...formData,
      name: formData.name.toUpperCase() ?? "",
    };

    try {

      if (isEditing) {
        await api.put(`/persons/update/${formData.id}`, payload);
      } else {
        await api.post("/persons/create", payload);
      }

      // Si no hay errores → cerrar modal
      fetchPersons();
      handleClose();

    } catch (err) {
      // Si Laravel envía errores de validación (422)
      if (err.response?.status === 422 && err.response.data?.errors) {
        setErrors(err.response.data.errors); // ← objeto EXACTO de Laravel
      } else {
        setErrors({ general: ["Error desconocido al guardar"] });
      }
      Swal.fire({
        title: 'Error',
        text: "Error desconocido al guardar",
        icon: 'error',
        timer: 1500,
        showConfirmButton: false
      });

    } finally {
      setLoadingSave(false);
    }
  };

  const handleSetupTotp = async (person) => {
    try {
      setLoadingTotp(person.id);

      const res = await api.post(`/persons/${person.id}/totp/setup`);

      setTotpData({
        name: person.name,
        otpauth: res.data.otpauth,
        secret: res.data.secret
      });

      // 🔥 MARCAR inmediatamente el secret en memoria
      const updated = persons.map((p) =>
        p.id === person.id ? { ...p, totp_secret: res.data.secret } : p
      );

      setPersons(updated);
      setFilteredPersons(updated);

      setShowTotpModal(true);

    } catch (err) {
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.error || "Error configurando TOTP",
        icon: 'error',
        timer: 1500,
        showConfirmButton: false
      });
      console.log(err.response?.data?.error || "Error configurando TOTP");
    } finally {
      setLoadingTotp(null);
    }
  };

  const openVehiclesModal = async (personId) => {
    setShowVehiclesModal(true);
    setLoadingVehicles(true);
    setVehicles([]);

    try {
      const res = await api.get(`/persons/${personId}/vehicles`);
      setVehicles(res.data.data);
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "No se pudieron cargar los vehículos."
      });
    } finally {
      setLoadingVehicles(false);
    }
  };

  // ==============================
  // Columnas tabla
  // ==============================
  const columns = [
    { name: "Empresa", selector: (row) => row.tenant?.name, sortable: true },
    { name: "Nombre", selector: (row) => row.name, sortable: true },
    { name: "Tipo Doc", selector: (row) => row.document_type, sortable: true },
    { name: "Documento", selector: (row) => row.document_num, sortable: true },
    { name: "Teléfono", selector: (row) => row.mobile_number, sortable: true },
    {
      name: "Estado",
      cell: (row) => (
        <span
          className={`badge ${row.is_active ? "bg-success" : "bg-danger"}`}
          style={{ fontSize: "0.75rem" }}
        >
          {row.is_active ? "Activo" : "Inactivo"}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Opciones",
      cell: (row) => (
        <div className="d-flex gap-2">
          {/* Editar */}
          <Button 
            variant="outline-info"
            size="sm"
            onClick={() => handleShowEdit(row)}
          >
            <PencilSquare />
          </Button>

          {/* Configurar TOTP solo si es conductor y no tiene secreto */}
          {row.is_driver && !row.totp_secret && row.is_active && (
            <Button
              variant="outline-warning"
              size="sm"
              disabled={loadingTotp === row.id}
              onClick={() => handleSetupTotp(row)}
            >
              {loadingTotp === row.id ? (
                <span className="spinner-border spinner-border-sm"></span>
              ) : (
                <QrCodeScan />
              )}
            </Button>
          )}

          {row.is_driver && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => openVehiclesModal(row.id)}
            >
              <CarFront />
            </Button>
          )}

        </div>
      ),
      ignoreRowClick: true,
    },
  ];

  return (
    <div className="mt-4">
      {/* TABLA */}
      <DataTable
        title="Personas"
        columns={columns}
        data={filteredPersons}
        pagination
        highlightOnHover
        responsive
        striped
        progressPending={loadingTable}
        progressComponent={
          <div className="text-center p-3">
            <div className="spinner-border text-primary"></div>
            <p className="mt-2">Cargando datos...</p>
          </div>
        }
        subHeader
        subHeaderComponent={
          <div className="d-flex justify-content-between w-100">
            <Button variant="outline-success" size="sm" onClick={handleShowNew}>
              Nuevo <Plus />
            </Button>

            <input
              type="text"
              className="form-control w-25"
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        }
      />

      {/* MODAL */}
      <Modal show={showModal} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? "Editar Persona" : "Nueva Persona"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
         
          <Form>
            {/* TENANT */}
            <Form.Group className="mb-3">
              <Form.Label>Empresa</Form.Label>
              <Form.Select
                value={formData.tenant_id}
                onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
              >
                <option value="">Seleccionar...</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Form.Select>
              {errors.tenant_id?.map((e, i) => (
                <Form.Text key={i} className="text-danger d-block">{e}</Form.Text>
              ))}
            </Form.Group>

            {/* NAME */}
            <Form.Group className="mb-3">
              <Form.Label>Nombre completo</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              {errors.name?.map((e, i) => (
                <Form.Text key={i} className="text-danger d-block">{e}</Form.Text>
              ))}
            </Form.Group>

            {/* DOCUMENT TYPE */}
            <div className="row">
              <Form.Group className="mb-3 col-6">
                <Form.Label>Tipo de documento</Form.Label>
                <Form.Select
                  value={formData.document_type}
                  onChange={(e) =>
                    setFormData({ ...formData, document_type: e.target.value })
                  }
                >
                  <option value="">Seleccione...</option>
                  <option value="CC">Cédula</option>
                  <option value="TI">Tarjeta de Identidad</option>
                  <option value="CE">Cédula de Extranjería</option>
                  <option value="PASSPORT">Pasaporte</option>
                </Form.Select>

                {errors.document_type?.map((e, i) => (
                  <Form.Text key={i} className="text-danger d-block">{e}</Form.Text>
                ))}
              </Form.Group>

              {/* DOCUMENT NUMBER */}
              <Form.Group className="mb-3 col-6">
                <Form.Label>Número de documento</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.document_num}
                  onChange={(e) =>
                    setFormData({ ...formData, document_num: e.target.value })
                  }
                />
                {errors.document_num?.map((e, i) => (
                  <Form.Text key={i} className="text-danger d-block">{e}</Form.Text>
                ))}
              </Form.Group>
            </div>

            <div className="row">
              {/* PHONE */}
              <Form.Group className="mb-3 col-6">
                <Form.Label>Teléfono fijo</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                />
                {errors.phone?.map((e, i) => (
                  <Form.Text key={i} className="text-danger d-block">{e}</Form.Text>
                ))}
              </Form.Group>

              {/* MOBILE */}
              <Form.Group className="mb-3 col-6">
                <Form.Label>Celular</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.mobile_number}
                  onChange={(e) =>
                    setFormData({ ...formData, mobile_number: e.target.value })
                  }
                />
                {errors.mobile_number?.map((e, i) => (
                  <Form.Text key={i} className="text-danger d-block">{e}</Form.Text>
                ))}
              </Form.Group>
            </div>
            
            <div className="row">
              {/* ADDRESS */}
              <Form.Group className="mb-3 col-6">
                <Form.Label>Dirección</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                />
                {errors.address?.map((e, i) => (
                  <Form.Text key={i} className="text-danger d-block">{e}</Form.Text>
                ))}
              </Form.Group>

              {/* CITY */}
              <Form.Group className="mb-3 col-6">
                <Form.Label>Ciudad</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.city}
                  onChange={(e) =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                />
                {errors.city?.map((e, i) => (
                  <Form.Text key={i} className="text-danger d-block">{e}</Form.Text>
                ))}
              </Form.Group>
            </div>
            
            <div className="row">
              {/* EMAIL */}
              <Form.Group className="mb-3 col-6">
                <Form.Label>Correo electrónico</Form.Label>
                <Form.Control
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
                {errors.email?.map((e, i) => (
                  <Form.Text key={i} className="text-danger d-block">{e}</Form.Text>
                ))}
              </Form.Group>

              {/* LICENSE NUMBER */}
              <Form.Group className="mb-3 col-6">
                <Form.Label>Número de licencia</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.license_num}
                  onChange={(e) =>
                    setFormData({ ...formData, license_num: e.target.value })
                  }
                />
                {errors.license_num?.map((e, i) => (
                  <Form.Text key={i} className="text-danger d-block">{e}</Form.Text>
                ))}
              </Form.Group>
            </div>
            
            <div className="row">
              {/* EPS */}
              <Form.Group className="mb-3 col-6">
                <Form.Label>EPS</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.eps}
                  onChange={(e) =>
                    setFormData({ ...formData, eps: e.target.value })
                  }
                />
                {errors.eps?.map((e, i) => (
                  <Form.Text key={i} className="text-danger d-block">{e}</Form.Text>
                ))}
              </Form.Group>

              {/* ARL */}
              <Form.Group className="mb-3 col-6">
                <Form.Label>ARL</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.arl}
                  onChange={(e) =>
                    setFormData({ ...formData, arl: e.target.value })
                  }
                />
                {errors.arl?.map((e, i) => (
                  <Form.Text key={i} className="text-danger d-block">{e}</Form.Text>
                ))}
              </Form.Group>
            </div>
            
            <div className="row">
              {/* AFP */}
              <Form.Group className="mb-3 col-6">
                <Form.Label>AFP</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.afp}
                  onChange={(e) =>
                    setFormData({ ...formData, afp: e.target.value })
                  }
                />
                {errors.afp?.map((e, i) => (
                  <Form.Text key={i} className="text-danger d-block">{e}</Form.Text>
                ))}
              </Form.Group>

              {/* CONTRACT TYPE */}
              <Form.Group className="mb-3 col-6">
                <Form.Label>Tipo de vinculación</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.contract_type}
                  onChange={(e) =>
                    setFormData({ ...formData, contract_type: e.target.value })
                  }
                />
                {errors.contract_type?.map((e, i) => (
                  <Form.Text key={i} className="text-danger d-block">{e}</Form.Text>
                ))}
              </Form.Group>
            </div>
            

            {/* CHECKBOXES */}
            <div className="d-flex gap-3 mb-3">
              <Form.Check
                type="checkbox"
                label="Conductor"
                checked={formData.is_driver}
                onChange={(e) =>
                  setFormData({ ...formData, is_driver: e.target.checked })
                }
              />
              <Form.Check
                type="checkbox"
                label="Administrador"
                checked={formData.is_admin}
                onChange={(e) =>
                  setFormData({ ...formData, is_admin: e.target.checked })
                }
              />
              <Form.Check
                type="checkbox"
                label="Propietario"
                checked={formData.is_owner}
                onChange={(e) =>
                  setFormData({ ...formData, is_owner: e.target.checked })
                }
              />
              <Form.Check
                type="checkbox"
                label="Activo"
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData({ ...formData, is_active: e.target.checked })
                }
              />
            </div>
            {errors.roles?.map((e, i) => (
              <Form.Text key={i} className="text-danger d-block">{e}</Form.Text>
            ))}

          </Form>

        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancelar
          </Button>

          <Button variant="primary" onClick={handleSave} disabled={loadingSave}>
            {loadingSave ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Guardando...
              </>
            ) : (
              isEditing ? "Actualizar" : "Guardar"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showTotpModal} onHide={() => setShowTotpModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Configurar Google Authenticator</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {totpData && (
            <>
              <p><strong>Conductor:</strong> {totpData.name}</p>

              <div className="text-center mb-3">
                <QRCodeCanvas
                  value={totpData.otpauth}
                  size={230}
                  level="H"
                  bgColor="#ffffff"
                  fgColor="#000000"
                />
              </div>

              <p className="text-muted">
                Escanee este código con la app <strong>Google Authenticator</strong>.
              </p>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTotpModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showVehiclesModal} onHide={() => setShowVehiclesModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Vehículos asignados</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loadingVehicles ? (
            <div className="text-center my-3">
              <span className="spinner-border spinner-border-sm"></span>
              <p className="mt-2">Cargando datos...</p>
            </div>
          ) : vehicles.length === 0 ? (
            <p className="text-muted">Esta persona no tiene vehículos asignados.</p>
          ) : (
            <ul className="list-group">
              {vehicles.map((v) => (
                <li key={v.id} className="list-group-item">
                  <strong>{v.plate}</strong> — {v.brand} {v.model} ({v.lateral})
                </li>
              ))}
            </ul>
          )}
        </Modal.Body>
      </Modal>

    </div>
  );
}
